// BHP-1083: domain-availability check over Backend2.0
// (`GET /api/domain-availability?name=<sld>&tlds[]=...`). Plain JSON (not Hydra),
// so we call httpClient directly and parse the array. Replaces the legacy
// `POST /api/v1/domain/check`.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { httpClient } from '../../../shared/http/httpClient';
import { ApiError } from '../../../shared/http/ApiError';
import type { AvailabilityCheck, DomainAvailability } from '../domain';

export const availabilityKeys = {
  all: ['domain-availability'] as const,
  query: (name: string, tlds: readonly string[]) =>
    [...availabilityKeys.all, name, [...tlds].sort().join(',')] as const
};

/** Build `?name=<sld>&tlds[]=a&tlds[]=b` — httpClient's params can't repeat keys. */
function buildQuery(name: string, tlds: readonly string[]): string {
  const search = new URLSearchParams();
  search.set('name', name);
  tlds.forEach((tld) => search.append('tlds[]', tld));
  return search.toString();
}

async function requestAvailability(
  name: string,
  tlds: readonly string[]
): Promise<DomainAvailability[]> {
  return await httpClient.get<DomainAvailability[]>(
    `/domain-availability?${buildQuery(name, tlds)}`,
    { accept: 'application/json' }
  );
}

/**
 * The backend validates every requested TLD against the subscription catalogue
 * and rejects the WHOLE batch with 422 when one of them is unknown
 * (DomainAvailabilityService::check). One missing catalogue row would otherwise
 * blank the entire check page.
 */
function isBatchRejected(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422;
}

/**
 * Retry TLD by TLD and keep whatever the backend does support. This costs one
 * request per TLD against the 30/min per-IP rate limit, so it runs only on the
 * 422 path — the happy path stays a single batched request.
 *
 * BHP-1128: a TLD that 422s on its own is one we do not carry, and that is now
 * reported instead of filtered away — it is the only way the page can explain
 * why a typed extension is missing. Any other failure (500, network) is not a
 * verdict about our catalogue, so it is left out of `unsupportedTlds` rather
 * than mislabelled; as before, that TLD simply has no row.
 */
async function fetchPerTld(
  name: string,
  tlds: readonly string[]
): Promise<AvailabilityCheck> {
  const settled = await Promise.allSettled(
    tlds.map(async (tld) => await requestAvailability(name, [tld]))
  );

  const results: DomainAvailability[] = [];
  const unsupportedTlds: string[] = [];

  settled.forEach((outcome, index) => {
    if (outcome.status === 'fulfilled') {
      results.push(...outcome.value);
      return;
    }

    if (isBatchRejected(outcome.reason)) {
      unsupportedTlds.push(tlds[index] ?? '');
    }
  });

  return { results, unsupportedTlds };
}

export async function fetchDomainAvailability(
  name: string,
  tlds: readonly string[]
): Promise<AvailabilityCheck> {
  try {
    return {
      results: await requestAvailability(name, tlds),
      unsupportedTlds: []
    };
  } catch (error) {
    // A single requested TLD keeps rethrowing: there is nothing left to show
    // next to the refusal. The check page always asks for the default set as
    // well (BHP-1122), so it never lands here.
    if (!isBatchRejected(error) || tlds.length <= 1) {
      throw error;
    }
    const check = await fetchPerTld(name, tlds);
    if (check.results.length === 0) {
      throw error;
    }
    return check;
  }
}

export function useDomainAvailability(
  name: string,
  tlds: readonly string[],
  enabled: boolean
): UseQueryResult<AvailabilityCheck> {
  return useQuery({
    queryKey: availabilityKeys.query(name, tlds),
    queryFn: async () => await fetchDomainAvailability(name, tlds),
    enabled: enabled && name.length > 0 && tlds.length > 0
  });
}
